import type { AbstractIntlMessages } from "next-intl";

/**
 * Website pages that ship trimmed client messages. Exact paths: unknown ones hit the app catch-all.
 * Missing page here only costs speed (full catalog), never text.
 */
const WEBSITE_PAGES: ReadonlySet<string> = new Set([
  "/",
  "/preise",
  "/demo",
  // pushState after booking; missing here = IntlProvider reload, booked state lost.
  "/demo/thanks",
  "/impressum",
  "/datenschutz",
  "/partner",
  "/migration-service",
  "/features/inbox",
  "/features/e-signature",
  "/features/phone",
  "/features/ai-answering-service",
  "/features/documents",
  "/features/search",
  "/features/invoice-approval",
  "/features/audit-proof-archive",
  "/features/team-chat",
  "/features/mailings",
  "/features/owners-meetings",
  "/features/customer-portal",
  // Content-built pages (features/_components/content); a new menu entry without page lands on features/[slug].
  "/features/properties",
  "/features/contacts",
  "/features/leases",
  "/features/meters",
  "/features/keys",
  "/features/maintenance",
  "/features/rent-increases",
  "/features/bank-connection",
  "/features/rent-collection",
  "/features/dunning",
  "/features/budget-plan",
  "/features/annual-statement",
  "/features/service-charges",
  "/features/forms",
  "/features/booking",
  "/features/help-center",
  "/features/tasks",
  "/features/calendar",
  "/features/time-tracking",
  "/features/knowledge",
  "/features/clips",
  "/features/learning",
  "/features/api",
  "/features/ai-agents",
  "/features/customer-chat",
  "/features/ai-calls",
  "/features/document-agent",
  "/features/invoice-agent",
  // Switch pages, same content renderer.
  "/integrations/impower",
  "/integrations/domus",
  "/integrations/immoware24",
  "/integrations/powerhaus",
  "/compare",
  "/compare/casavi",
  "/compare/impower",
  "/compare/facilioo",
  "/compare/idwell",
  "/compare/domus",
  "/compare/immoware24",
  "/compare/powerhaus",
  "/compare/easimo",
  "/careers",
  "/careers/en",
  "/careers/engineering",
  "/careers/engineering2",
  "/careers/en/engineering",
  "/careers/developers",
  "/careers/developers/en",
  "/careers/en/developers",
]);

/** Client messages of website pages; the full catalog would ship MBs in every HTML. */
// New namespace in a website client component: add it here, else humanized keys show.
const WEBSITE_NAMESPACES = ["marketing", "public"] as const;

const WEBSITE_SUBKEYS: Readonly<Record<string, readonly string[]>> = {
  common: [
    "actions",
    "bookingEmbedIframe",
    "locales",
    "notFound",
    "updateAvailableBanner",
  ],
  shared: ["appShortcuts", "documentScan"],
};

export function isWebsitePage(pathname: string): boolean {
  return WEBSITE_PAGES.has(pathname);
}

function isMessageGroup(value: unknown): value is AbstractIntlMessages {
  return typeof value === "object" && value !== null;
}

export function pickWebsiteMessages(
  messages: AbstractIntlMessages,
): AbstractIntlMessages {
  const picked: AbstractIntlMessages = {};
  for (const namespace of WEBSITE_NAMESPACES) {
    const group = messages[namespace];
    if (group !== undefined) picked[namespace] = group;
  }
  for (const [namespace, keys] of Object.entries(WEBSITE_SUBKEYS)) {
    const group = messages[namespace];
    if (!isMessageGroup(group)) continue;
    const subset: AbstractIntlMessages = {};
    for (const key of keys) {
      const value = group[key];
      if (value !== undefined) subset[key] = value;
    }
    picked[namespace] = subset;
  }
  return picked;
}
